import uuid from 'react-uuid';
import {
  fetchESignPdf, fetchS3Data,
  deleteFileFromS3, saveDataToS3,
  generateEsignPdfAndUpload,
} from './utilService';
import {
  pdfBlobConversion, generateAdditionalTables, getUniqueFileId,
  convertFileSizeToMB, base64ToBlobConversion,
  chunkString, removeTabSpaces,
  stringifyObjectValues, encryptPayload,
} from './util';

const getVal = require('lodash/get');

const ESIGN_PDF_GENERATION_FAILED_STATUS = 'PDF_FAILED';
const ESIGN_PDF_GENERATION_SUCCESS_STATUS = 'PDF_SUCCESS';

export default class EsignUtils {
  constructor(...args) {
    const [elementObj,
      formContainer,
      formSubmissionPayload,
      s3FileInfo,
      setS3FileInfo,
      setAllS3FileIds,
      iformIntegrationUrls,
      contextVal,
      setS3EsignFileInfo,
      setIsFormSubmission,
      apiResponse,
      setApiResponse,
      loggerApiCall,
      objectOfFunctions,
      journeyId,
      payloadFields,
      setFileData,
      putDataToS3,
      setEsignLoadingState,
      saveItrackerStatusApi] = args;
    this.elementObj = elementObj;
    this.formContainer = formContainer;
    this.formSubmissionPayload = formSubmissionPayload;
    this.s3FileInfo = s3FileInfo;
    this.setS3FileInfo = setS3FileInfo;
    this.setAllS3FileIds = setAllS3FileIds;
    this.iformIntegrationUrls = iformIntegrationUrls;
    this.contextVal = contextVal;
    this.setS3EsignFileInfo = setS3EsignFileInfo;
    this.setIsFormSubmission = setIsFormSubmission;
    this.apiResponse = apiResponse;
    this.setApiResponse = setApiResponse;
    this.loggerApiCall = loggerApiCall;
    this.objectOfFunctions = objectOfFunctions;
    this.journeyId = journeyId;
    this.payloadFields = payloadFields;
    this.setFileData = setFileData;
    this.putDataToS3 = putDataToS3;
    this.setEsignLoadingState = setEsignLoadingState;
    this.saveItrackerStatusApi = saveItrackerStatusApi;
  }

    trackEsignStatus = (status) => {
      if (!this.saveItrackerStatusApi) {
        return;
      }
      this.saveItrackerStatusApi(status).catch((error) => {
        this.loggerApiCall(
          `status=saveItrackerStatusApiCallFailed pageStatus=${status} message=${error?.message || error}`,
          'error'
        );
      });
    };

  /**
     * Split a given text into multiple lines without breaking words
     * ensuring that each line does not exceed the specified character limit.
     * @param {string} text - The input string to be wrapped.
     * @param {number} maxCharsPerLine - Maximum number of characters allowed per line.
     * @returns {string} The text broken into lines with '\n' as separator
     */
  wrapTextWithoutBreakWords = (text, maxCharsPerLine) => {
    const words = text.replace(/\n/g, '').split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach((word) => {
      if ((currentLine + word).length > maxCharsPerLine) {
        lines.push(currentLine.trim());
        currentLine = `${word} `;
      } else {
        currentLine += `${word} `;
      }
    });
    if (currentLine.trim()) {
      lines.push(currentLine.trim());
    }
    return lines.join('\n');
  };

  getAdditionalTableData = (operations) => {
    let additionalTableData = null;
    if (operations) {
      // select data to create tables and call function to create tables
      let dataToProcess = [];
      const tableFormData = {};
      operations.dataKeys.forEach((key) => {
        const data = getVal(this.objectOfFunctions, key);

        if (!data) {
          return;
        }

        const eSignOperations = this.formContainer.operations
          && this.formContainer.operations.generateESignPdf
          ? this.formContainer.operations.generateESignPdf
          : null;
        if (eSignOperations && eSignOperations.splitFields
          && eSignOperations.splitFields.length > 0) {
          eSignOperations.splitFields.forEach((ele) => {
            ele.newFields.forEach((fieldObj) => {
              data.forEach((row, dindex) => {
                const currentFieldVal = getVal(row, fieldObj.currentField);
                if (currentFieldVal) {
                  let splitArr = [];
                  if (fieldObj.splitChar) {
                    splitArr = currentFieldVal.trim().split(fieldObj.splitChar);
                  }

                  if (fieldObj.splitCharLength) {
                    let cField = currentFieldVal;
                    if (fieldObj.index && splitArr[fieldObj.index]) {
                      cField = splitArr[fieldObj.index];
                    }
                    splitArr = chunkString(cField.trim(), fieldObj.splitCharLength);
                  }

                  if (fieldObj.splitInTwoParts) {
                    splitArr.push(currentFieldVal.trim().slice(0, fieldObj.splitInTwoParts));
                    splitArr.push(currentFieldVal.trim().slice(fieldObj.splitInTwoParts));
                  }

                  if (fieldObj.splitFieldsName.length > splitArr.length) {
                    const diff = fieldObj.splitFieldsName.length - splitArr.length;
                    // eslint-disable-next-line no-plusplus -- desc
                    for (let i = 0; i < diff; i++) {
                      splitArr.unshift(' ');
                    }
                  }

                  fieldObj.splitFieldsName.forEach((field, index) => {
                    if (splitArr[index]) {
                      data[dindex][field] = splitArr[index].trim();
                    }
                  });
                }
              });
            });
          });
        }

        tableFormData[key] = data;
        if (Array.isArray(data)) {
          if (data && data.length > 0) {
            dataToProcess = [...dataToProcess, ...data];
          }
        } else {
          Object.keys(data).forEach((dataKey) => {
            if (!data[dataKey]) {
              delete data[dataKey];
            }
          });
          if (Object.keys(data).length > 0) {
            dataToProcess = [...dataToProcess, data];
          }
        }
      });

      const tables = generateAdditionalTables(
        dataToProcess,
        operations.tableTemplatesToRun,
        {
          isOrder: operations.isOrder,
          ensurePayloadOrder: operations.ensurePayloadOrder,
          sendSeparateTables: operations.sendSeparateTables,
          enhanceIndexLabeling: operations.enhanceIndexLabeling,
        }
      );

      // page number to add these additional tables on pdf
      const { page } = operations;
      if (tables.length > 0) {
        additionalTableData = { page, tables };
        if (operations?.isAdditionalTableLayoutPreDefined) {
          additionalTableData = { ...additionalTableData, tableFormData };
        }
      }
    }
    return additionalTableData;
  };

  getEsignPdfFromS3 = async () => {
    if (!this.formContainer?.operations?.generateESignPdf
      ?.isS3UploadRequiredForEsignPdf?.isPreSignPdfLoad) {
      return { status: false };
    }

    const fileDataInfo = this.s3FileInfo.find((fileInfo) => fileInfo.fileFrom === 'preSignPdf');
    if (!fileDataInfo) {
      return { status: false };
    }

    const payloadObj = {
      head: {
        traceId: uuid(),
        span: 'iForm6513',
      },
      body: {
        docKey: fileDataInfo.fileId,
      },
    };

    const paramsObj = {
      iformIntegrationUrls: this.iformIntegrationUrls,
      pageType: this.formContainer.pageType,
      cuid: this.contextVal.cuid,
      requestPayload: payloadObj,
    };

    try {
      const response = await fetchS3Data(paramsObj, 'file');
      const data = await response.json();

      if (data?.statusIndicator === 'T') {
        return { data, status: true };
      }
    } catch (error) {
      console.error('Error loading eSign PDF from S3:', error);
    }

    return { status: false };
  };

  deleteFromPayload = (eSignOperations, requestPayload) => {
    if (eSignOperations && eSignOperations.deleteFromPayload) {
      eSignOperations.deleteFromPayload.forEach((key) => {
        delete requestPayload.body[key];
      });
    }
  }

  applyTextInfoMappings = (tableInfoJson, payload) => {
    if (!tableInfoJson || typeof tableInfoJson !== 'object' || Array.isArray(tableInfoJson)) {
      return tableInfoJson;
    }
    const result = {};
    Object.entries(tableInfoJson).forEach(([label, config]) => {
      if (config?.textInfo?.text && config?.textInfo?.mapping) {
        const resolvedText = Object.entries(config.textInfo.mapping).reduce(
          (text, [placeholder, payloadKey]) => {
            const value = payload[payloadKey] ?? '';
            return text.replace(new RegExp(`\\{${placeholder}\\}`, 'g'), value);
          },
          config.textInfo.text
        );
        result[label] = {
          ...config,
          textInfo: { ...config.textInfo, text: resolvedText },
        };
      } else {
        result[label] = config;
      }
    });
    return result;
  };

  getEsignPdfFromS3 = async () => {
    if (!this.formContainer?.operations?.generateESignPdf
      ?.isS3UploadRequiredForEsignPdf?.isPreSignPdfLoad) {
      return { status: false };
    }

    const fileDataInfo = this.s3FileInfo.find((fileInfo) => fileInfo.fileFrom === 'preSignPdf');
    if (!fileDataInfo) {
      return { status: false };
    }

    const payloadObj = {
      head: {
        traceId: uuid(),
        span: 'iForm6513',
        journeyId: this.journeyId,
      },
      body: {
        docKey: fileDataInfo.fileId,
      },
    };

    const paramsObj = {
      iformIntegrationUrls: this.iformIntegrationUrls,
      pageType: this.formContainer.pageType,
      cuid: this.contextVal.cuid,
      requestPayload: payloadObj,
    };

    try {
      const response = await fetchS3Data(paramsObj, 'file');
      const data = await response.json();

      if (data?.statusIndicator === 'T') {
        return { data, status: true };
      }
    } catch (error) {
      console.info('Error loading eSign PDF from S3:', error);
    }

    return { status: false };
  };

  /**
   * Pdf generate api call to display pdf for esign
   */
  /* eslint complexity: ["error", 26] -- need to increase complexity */
  getESignPDF = async (additionalProps) => {
    const {
      isSignBoxRequired, pdfSignatureOptions, signBoxHeading, skipPreDefinedTable,
      isEncryptionRequired, esignPdfFileId,
    } = additionalProps;
    let newFormSubmissionPayload = { ...this.formSubmissionPayload };
    const eSignOperations = this.formContainer.operations
      && this.formContainer.operations.generateESignPdf
      ? this.formContainer.operations.generateESignPdf
      : null;

    // staticTableFields
    if (eSignOperations && eSignOperations.staticTableFields) {
      Object.keys(eSignOperations.staticTableFields).forEach((key) => {
        const obj = eSignOperations.staticTableFields[key];
        if (obj.wordWrap && newFormSubmissionPayload[key]) {
          newFormSubmissionPayload[key] = newFormSubmissionPayload[key].replace(/\n/g, '');
          const containerWidth = obj.containerWidth || 260;
          const fontSize = obj.fontSize || 12;
          const maxChars = Math
            .floor(containerWidth / (fontSize * 0.6));
          newFormSubmissionPayload[key] = this.wrapTextWithoutBreakWords(
            newFormSubmissionPayload[key],
            maxChars
          );
        }
      });
    }

    let additionalTablesData = null;
    let pdfStyles = {};
    let additionalFields = {};
    // if additional dynamic tables are there
    if (eSignOperations && eSignOperations.generateAdditionalTables) {
      additionalTablesData = this.getAdditionalTableData(
        eSignOperations.generateAdditionalTables);
      pdfStyles = eSignOperations.generateAdditionalTables.style
        ? eSignOperations.generateAdditionalTables.style : {};
    }

    if (eSignOperations && eSignOperations.generateOptionLabelForEsign
      && eSignOperations.generateOptionLabelForEsign.length > 0) {
      Object.keys(eSignOperations.generateOptionLabelForEsign[0]).forEach((key) => {
        if (this.formSubmissionPayload[key]) {
          const esignPdf = eSignOperations.generateOptionLabelForEsign[0];
          const labelval = esignPdf[key][this.formSubmissionPayload[key]];
          newFormSubmissionPayload[key] = labelval;
        }
      });
    }

    // if we have to pass additional fields by combining other fields
    if (eSignOperations && eSignOperations.generateCombinedFields
      && eSignOperations.generateCombinedFields.length > 0) {
      eSignOperations.generateCombinedFields.forEach((ele) => {
        const dataObj = { ...this.objectOfFunctions[ele.fieldsFrom] };
        ele.newFields.forEach((fieldObj) => {
          let newField = '';
          fieldObj.fieldsToCombine.forEach((field, index) => {
            let val = getVal(dataObj, field);
            val = val && val !== '' ? val.trim() : null;
            if (val) {
              newField += (index > 0) && (newField !== '') ? `${fieldObj.combineChar}${val}` : `${val}`;
            }
          });
          if (newField !== '') {
            additionalFields[fieldObj.addAs] = newField.trim();
          }
        });
      });
    }

    // if we have to pass additional fields by spliting current fields
    if (eSignOperations && eSignOperations.splitFields
      && eSignOperations.splitFields.length > 0) {
      eSignOperations.splitFields.forEach((ele) => {
        const dataObj = { ...this.objectOfFunctions[ele.fieldsFrom] };
        ele.newFields.forEach((fieldObj) => {
          const currentFieldVal = getVal(dataObj, fieldObj.currentField);
          if (currentFieldVal) {
            let splitArr = [];
            if (fieldObj.splitChar) {
              splitArr = currentFieldVal.trim().split(fieldObj.splitChar);
            }

            if (fieldObj.splitCharLength) {
              let cField = currentFieldVal;
              if (fieldObj.index && splitArr[fieldObj.index]) {
                cField = splitArr[fieldObj.index];
              }
              splitArr = chunkString(cField.trim(), fieldObj.splitCharLength);
            }

            if (fieldObj.splitInTwoParts) {
              splitArr.push(currentFieldVal.trim().slice(0, fieldObj.splitInTwoParts));
              splitArr.push(currentFieldVal.trim().slice(fieldObj.splitInTwoParts));
            }

            if (fieldObj.splitFieldsName.length > splitArr.length) {
              const diff = fieldObj.splitFieldsName.length - splitArr.length;
              // eslint-disable-next-line no-plusplus -- desc
              for (let i = 0; i < diff; i++) {
                splitArr.unshift(' ');
              }
            }

            fieldObj.splitFieldsName.forEach((field, index) => {
              if (splitArr[index]) {
                newFormSubmissionPayload[field] = splitArr[index].trim();
              }
            });
          }
        });
      });
    }

    // Remove tab space from the object
    newFormSubmissionPayload = removeTabSpaces(newFormSubmissionPayload);
    additionalFields = removeTabSpaces(additionalFields);

    /**
     * To do: send specific keys from formSubmissionPayload to the eSign API,
     */
    // create eSignPayload
    const requestPayload = {
      head: {
        traceId: uuid(),
        span: 'iForm6513',
        journeyId: this.journeyId,
      },
      body: {
        ...this.payloadFields,
        ...newFormSubmissionPayload,
        ...additionalFields,
        isSignBoxRequired,
        isEncryptionRequired,
        pdfSignatures: JSON.stringify(pdfSignatureOptions),
        signBoxHeading,
        skipPreDefinedTable,
        pdfStyles: JSON.stringify(
          eSignOperations?.pdfStyle ? eSignOperations.pdfStyle : pdfStyles
        ),
      },
    };
    // delete cvv from requestPayload if it exists
    delete requestPayload.body.cvv;
    if (additionalTablesData) {
      const additionalTablePayload = { ...additionalTablesData };
      const additionalTableInfoJson = eSignOperations?.additionalTableInfoJson;
      const isAdditionalInfoTableNonEmpty = Array.isArray(additionalTableInfoJson)
        ? additionalTableInfoJson.length > 0
        // eslint-disable-next-line unicorn/no-nested-ternary -- desc
        : typeof additionalTableInfoJson === 'object' && additionalTableInfoJson !== null
          ? Object.keys(additionalTableInfoJson).length > 0
          : !!additionalTableInfoJson;

      if (isAdditionalInfoTableNonEmpty) {
        additionalTablePayload.additionalTableInfoJson = this
          .applyTextInfoMappings(additionalTableInfoJson, newFormSubmissionPayload);
      }

      requestPayload.body.additionalTables = JSON.stringify(additionalTablePayload);
    }
    if (this.formSubmissionPayload.dynamicClicDetails) {
      delete requestPayload.body.dynamicClicDetails;
    }

    // if esignPdfFileId is not null
    if (esignPdfFileId) {
      requestPayload.body.fileId = esignPdfFileId;
    }

    this.deleteFromPayload(eSignOperations, requestPayload);
    requestPayload.body = stringifyObjectValues(requestPayload.body);
    const params = {
      iformIntegrationUrls: this.iformIntegrationUrls,
      pageType: this.formContainer.pageType,
      cuid: this.contextVal.cuid,
      requestPayload,
    };
    const logString1 = 'status=calledESignApi';
    this.loggerApiCall(logString1, 'info');
    const data = esignPdfFileId
      ? await generateEsignPdfAndUpload(params)
      : await fetchESignPdf(params);

    return data;
  };

  /* eslint-disable no-restricted-syntax -- desc */
  getEsignElement = () => {
    let esignElem = {};
    for (const innerElements of this.elementObj) {
      for (const innerEle of innerElements.elements || []) {
        for (const childElem of innerEle.childElements || []) {
          if (childElem.fieldType === 'customTag' && childElem.customTag?.tagName === 'eSign') {
            esignElem = childElem;
            return esignElem; // Exit the loop early once the element is found
          }
        }
      }
    }
    return esignElem;
  }
  /* eslint-enable no-restricted-syntax -- desc */

  /**
   * This function is responsible for download required PDF on which signature is required
   */
  /* eslint-disable max-len -- need */
  /* eslint-disable-next-line max-params -- need */
  loadESignPDFData = async (childProps = null, isEsignPdfAttachToFiles = false, eSignPdf = null, signatures = null, totalPages = null, esignPdfFileId = null) => {
    if (!childProps) {
      childProps = this.getEsignElement();
    }

    let esignPdfLayout = eSignPdf;
    const isSignBoxRequired = !isEsignPdfAttachToFiles && childProps.customTag.childPropsStatic.isSignBoxRequired !== false;

    // clear previous response
    if (!signatures) {
      this.setApiResponse({});
    }

    // Load already exist esign pdf on S3
    const preSignPdf = await this.getEsignPdfFromS3();

    // if pdf generate with signature
    let pdfSignatureOptions = [];
    if (signatures && signatures.length > 0) {
      pdfSignatureOptions = childProps.customTag.childPropsStatic.pdfSignatureOptions;
      pdfSignatureOptions.every((signatureRow, index) => {
        pdfSignatureOptions[index].image = signatures[index].signature;
        pdfSignatureOptions[index].timestamp = signatures[index].timestamp;
        if (signatureRow.pageIndex === -1 && totalPages) {
          pdfSignatureOptions[index].pageIndex = totalPages - 1;
        }
        return true;
      });
    }

    // Pass the signature heading
    const signBoxHeading = childProps.customTag.childPropsStatic.signBoxHeading ? childProps.customTag.childPropsStatic.signBoxHeading : '';

    // Pass the skipPreDefinedTable
    const skipPreDefinedTable = childProps.customTag.childPropsStatic.skipPreDefinedTable ? childProps.customTag.childPropsStatic.skipPreDefinedTable : 0;

    // Pass the isEncryptedRequired
    const isEncryptionRequired = childProps.customTag.childPropsStatic.isEncryptionRequired ? childProps.customTag.childPropsStatic.isEncryptionRequired : 0;

    if (!preSignPdf.status || esignPdfFileId) {
      const res = await this.getESignPDF({
        isSignBoxRequired,
        pdfSignatureOptions,
        signBoxHeading,
        skipPreDefinedTable,
        isEncryptionRequired,
        esignPdfFileId,
      });
      if (res.status !== 200) {
        const logString1 = 'status=eSignApiCallFailed';
        this.loggerApiCall(logString1, 'error');
        this.trackEsignStatus(ESIGN_PDF_GENERATION_FAILED_STATUS);
        if (!isEsignPdfAttachToFiles) {
          this.setApiResponse({ response: 'error' });
        }
      } else {
        const logString1 = 'status=eSignApiCallSuccess';
        this.loggerApiCall(logString1, 'info');
        this.trackEsignStatus(ESIGN_PDF_GENERATION_SUCCESS_STATUS);
        const data = await res.json();
        if (esignPdfFileId) {
          esignPdfLayout = {};
        } else if (isEsignPdfAttachToFiles) {
          esignPdfLayout = data.file;
        } else {
          esignPdfLayout = {
            file: data.file,
            downloadPdfName: childProps.customTag.childPropsStatic.downloadPdfName
              ? childProps.customTag.childPropsStatic.downloadPdfName : '',
          };
        }

        if (esignPdfFileId) {
          this.setApiResponse({});
        } else {
          const blobUrl = pdfBlobConversion(data.file, 'application/pdf');
          const temp = { url: blobUrl };
          const response = { ...this.apiResponse, pdfResponse: temp };
          if (!signatures) {
            this.setApiResponse(response);
          }
        }
      }
    } else {
      if (isEsignPdfAttachToFiles) {
        esignPdfLayout = preSignPdf.data.file;
      } else {
        esignPdfLayout = {
          file: preSignPdf.data.file,
          downloadPdfName: childProps.customTag.childPropsStatic.downloadPdfName
            ? childProps.customTag.childPropsStatic.downloadPdfName : '',
        };
      }
      const blobUrl = pdfBlobConversion(preSignPdf.data.file, 'application/pdf');
      const temp = { url: blobUrl };
      const response = { ...this.apiResponse, pdfResponse: temp };
      if (!signatures) {
        this.setApiResponse(response);
      }
    }
    /* eslint-enable max-len -- need */
    return esignPdfLayout;
  };

  uploadEsignPdfToS3 = async (signedFile = null) => {
    this.setIsFormSubmission(true);

    const {
      isS3UploadRequiredForEsignPdf,
      appendStaticFieldsToPayload,
    } = this.formContainer.operations.generateESignPdf;
    if (!isS3UploadRequiredForEsignPdf?.page) {
      return Promise.resolve(true);
    }

    const deleteExistingFile = async () => {
      const fileDataInfo = this.s3FileInfo.find((fileInfo) => fileInfo.fileFrom === 'preSignPdf');
      if (!fileDataInfo) return;

      const deleteParamsObj = {
        iformIntegrationUrls: this.iformIntegrationUrls,
        fileId: fileDataInfo.fileId,
        pageType: this.formContainer.pageType,
        cuid: this.contextVal.cuid,
      };

      const deleteResponse = await deleteFileFromS3(deleteParamsObj);
      const deleteData = await deleteResponse.json();

      if (deleteData?.statusIndicator === 'T') {
        this.setS3FileInfo((prev) => prev.filter(
          (fileObj) => fileObj.fileId !== fileDataInfo.fileId)
        );
        this.setAllS3FileIds((prev) => prev.filter(
          (fileId) => fileId !== fileDataInfo.fileId)
        );
      }
    };

    const createEsignFile = async (esignPdfName) => {
      const base64EsignFile = await this.loadESignPDFData(null, true);
      const blobEsignFile = base64ToBlobConversion(base64EsignFile, 'application/pdf');
      return new File([blobEsignFile], esignPdfName, {
        lastModified: Date.now(),
        type: blobEsignFile.type,
      });
    };

    const createFileObject = (file, esignPdfName) => ({
      statusCode: 200,
      name: esignPdfName,
      fileFrom: 'preSignPdf',
      type: file ? file.type : '',
      size: file ? file.size : '',
      fileSizeMb: file ? convertFileSizeToMB(file.size) : '',
      documentTitle: esignPdfName,
      fileId: getUniqueFileId(this.formSubmissionPayload.docId, esignPdfName),
    });

    const createFormData = (fileObj, file) => {
      const formData = new FormData();
      const payloadObj = {
        head: {
          traceId: uuid(),
          span: 'iForm6513',
        },
        body: { ...fileObj },
      };
      formData.append('file', file || null);
      formData.append('data', encryptPayload(payloadObj));
      return formData;
    };

    const uploadNewFile = async () => {
      const esignFile = signedFile || await createEsignFile(
        isS3UploadRequiredForEsignPdf.esignPdfName
      );

      let fileObj = createFileObject(esignFile, isS3UploadRequiredForEsignPdf.esignPdfName);

      if (appendStaticFieldsToPayload) {
        fileObj = { ...fileObj, ...appendStaticFieldsToPayload };
      }

      if (isS3UploadRequiredForEsignPdf?.callSubmitApi) {
        fileObj.fileIds = [fileObj.fileId];
      }

      const formData = createFormData(fileObj, esignFile);

      const paramsObj = {
        iformIntegrationUrls: this.iformIntegrationUrls,
        formData,
        pageType: this.formContainer.pageType,
        cuid: this.contextVal.cuid,
        journeyId: this.journeyId,
        traceId: uuid(),
      };

      const data = await saveDataToS3(paramsObj);
      if (data?.statusIndicator === 'T') {
        this.setS3EsignFileInfo((prev) => [...prev, fileObj]);
      }
    };

    const uploadNewFileAsyncOp = async () => {
      const { esignPdfName } = isS3UploadRequiredForEsignPdf;
      const fileId = getUniqueFileId(this.formSubmissionPayload.docId, esignPdfName);
      await this.loadESignPDFData(null, true, null, null, null, fileId);
      const fileObj = {
        statusCode: 200,
        name: esignPdfName,
        fileFrom: 'preSignPdf',
        type: '',
        size: '',
        fileSizeMb: '',
        documentTitle: esignPdfName,
        fileId,
      };
      this.setS3EsignFileInfo((prev) => [...prev, fileObj]);
    };

    await deleteExistingFile();

    if (this.formContainer?.operations
      ?.generateESignPdf?.isS3UploadRequiredForEsignPdf?.isAsyncOperation) {
      await uploadNewFileAsyncOp();
    } else {
      await uploadNewFile();
    }

    return Promise.resolve(true);
  };

  getFinalPDFWithSignature = async (esignElement, pdfResponse) => {
    let file;
    const base64EsignFile = await this.loadESignPDFData(
      esignElement,
      false,
      null,
      pdfResponse.pdfSignatures,
      pdfResponse.totalPages
    );
    const blobEsignFile = base64ToBlobConversion(base64EsignFile.file, 'application/pdf');
    const filename = base64EsignFile.downloadPdfName !== '' ? `${base64EsignFile.downloadPdfName}.pdf` : 'esign_pdf_content.pdf';
    if (pdfResponse.type === 'download') {
      const a = window.document.createElement('a');
      a.href = window.URL.createObjectURL(
        new Blob([blobEsignFile], { type: blobEsignFile.type })
      );
      a.download = filename;
      // Append anchor to body.
      document.body.appendChild(a);
      a.click();
      // Remove anchor from body
      document.body.removeChild(a);
    } else {
      file = new File([blobEsignFile],
        filename,
        {
          lastModified: new Date().getTime(), type: blobEsignFile.type,
        });
    }
    return file;
  };

  /**
   * Add esign pdf to formSubmissionPayload and call the submit api
   */
  pdfSubmit = async (esignElement, pdfResponse) => {
    try {
      const {
        generateEsignPdfFromApi,
        pdfRequiredWithSignature,
        isS3UploadRequiredForEsignPdf,
        updateDownloadPdfStatus,
      } = this.formContainer?.operations?.generateESignPdf || {};
      let file;

      if (generateEsignPdfFromApi || pdfRequiredWithSignature) {
        this.setIsFormSubmission(true);
        file = await this.getFinalPDFWithSignature(esignElement, pdfResponse);
        this.setIsFormSubmission(false);
        if (pdfResponse.type === 'download') {
          if (updateDownloadPdfStatus) {
            this.putDataToS3(false, null, updateDownloadPdfStatus);
          }
          this.setEsignLoadingState(false);
          return;
        }
      } else {
        file = new File([pdfResponse.pdfOutput], 'esign_pdf_content.pdf', {
          lastModified: Date.now(),
          type: pdfResponse.pdfOutput.type,
        });
      }

      if (isS3UploadRequiredForEsignPdf?.required) {
        await this.uploadEsignPdfToS3(file);
      } else {
        this.setFileData(file);
      }
    } catch (error) {
      console.error('Error in pdfSubmit:', error);
      this.trackEsignStatus(ESIGN_PDF_GENERATION_FAILED_STATUS);
    }
  };
}
